import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useApi, errText } from "@/lib/api";
import { BackupCodes, CodeInput } from "@/components/account/BackupCodes";

export const MfaSetupDialog = ({ method, onClose }) => {
  const api = useApi();
  const qc = useQueryClient();
  const [info, setInfo] = useState(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    try { setInfo(await api.post("/mfa/setup", { method })); }
    catch (e) { toast.error(errText(e, "Couldn't start setup")); onClose(); }
    finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.post("/mfa/enable", { method, code });
      setCodes(r.backup_codes);
      toast.success("Two-step sign-in is on");
      qc.invalidateQueries({ queryKey: ["mfa"] });
    } catch (e) { toast.error(errText(e, "That code didn't work")); setCode(""); }
    finally { setBusy(false); }
  };
  const started = useRef(false);
  useEffect(() => { if (!started.current) { started.current = true; start(); } }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="mfa-setup-dialog" className="max-w-md bg-[#121212] border-[#2A2A2A] text-white">
        <DialogHeader>
          <DialogTitle>{codes ? "Save your backup codes" : method === "totp" ? "Set up an authenticator app" : "Set up email codes"}</DialogTitle>
          {!codes && <DialogDescription className="text-[#A3A3A3]">
            {method === "totp" ? "Scan this QR code with Google Authenticator, 1Password, Authy or similar, then enter the 6-digit code it shows."
              : info ? `We emailed a 6-digit code to ${info.sent_to}. Enter it below.` : "Sending a code…"}
          </DialogDescription>}
        </DialogHeader>
        {codes ? (
          <>
            <BackupCodes codes={codes} />
            <button data-testid="mfa-setup-done" onClick={onClose} className="rounded-full bg-[#00FF9D] py-2.5 text-sm font-semibold text-[#050505]">I've saved them</button>
          </>
        ) : info && (
          <>
            {method === "totp" && (
              <div className="flex flex-col items-center gap-3">
                <img data-testid="mfa-qr" alt="QR code for your authenticator app" className="h-44 w-44 rounded-lg bg-white p-2"
                  src={`data:image/svg+xml;utf8,${encodeURIComponent(info.qr_svg)}`} />
                <p className="text-[11px] text-[#6B6B6B]">Can't scan? Enter this key: <span data-testid="mfa-secret" className="font-mono text-[#A3A3A3] break-all">{info.secret}</span></p>
              </div>
            )}
            <CodeInput testid="mfa-setup-code" value={code} onChange={setCode} />
            <button data-testid="mfa-setup-confirm" onClick={confirm} disabled={busy || code.length < 6}
              className="rounded-full bg-[#00FF9D] py-2.5 text-sm font-semibold text-[#050505] disabled:opacity-40">{busy ? "Checking…" : "Turn on two-step sign-in"}</button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
